import Axios from 'axios';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, Card, Col, Collapse, Container, Form, FormLabel, Pagination, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faFileCsv, faFileExcel, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import config from '../../../config';
import Loading from '../../../components/Loading';
import { exportToCsv, exportToTxt } from '../../../utils/exportCsv';

function maskCpfCnpj(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11) return `${digits.substring(0, 3)}.***.***-**`;
  if (digits.length === 14) return `${digits.substring(0, 2)}.***.***/****-**`;
  return value;
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function formatExercise(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : String(date.getUTCFullYear());
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  return Number.isNaN(number) ? value : number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const exportColumns = [
  { header: 'Nº CDA', property: 'numeroCda' },
  { header: 'Tributo', property: 'tipoCda' },
  { header: 'CPF/CNPJ do devedor', property: 'cpfCnpjDevedor', format: maskCpfCnpj },
  { header: 'Nome do devedor', property: 'nomeDevedor' },
  { header: 'Exercício de incidência', property: 'exerciciosIncidencia' },
  { header: 'Data de inscrição', property: 'dataInscricao', format: formatDate },
  { header: 'Data de ajuizamento', property: 'dataAjuizamento', format: formatDate },
  { header: 'Localização', property: 'localizacao' },
  { header: 'Valor principal', property: 'valorPrincipal', format: formatMoney },
  { header: 'Multa', property: 'multa', format: formatMoney },
  { header: 'Juros', property: 'juros', format: formatMoney },
  { header: 'Valor total', property: 'valorTotal', format: formatMoney },
];

const tableColumns = exportColumns
  .filter((column) => ![
    'exerciciosIncidencia',
    'dataAjuizamento',
    'localizacao',
    'multa',
    'juros',
    'valorTotal',
  ].includes(column.property))
  .map((column) => {
    if (column.property === 'numeroCda') return { ...column, header: 'INSCRIÇÃO' };
    if (column.property === 'cpfCnpjDevedor') return { ...column, header: 'CPF/CNPJ' };
    if (column.property === 'dataInscricao') return { ...column, header: 'EXERCÍCIO', format: formatExercise };
    return column;
  });

const exportHeaders = exportColumns.map((column) => column.header);

function getExportRows(list) {
  return list.map((item) => exportColumns.map((column) => {
    const value = item[column.property];
    return column.format ? column.format(value) : (value === null || value === undefined ? '' : value);
  }));
}

function getQueryString(filters) {
  const params = new URLSearchParams();
  if (filters.nome.trim()) params.set('nome', filters.nome.trim());
  if (filters.exercicio) params.set('exercicio', filters.exercicio);
  return params.toString();
}

export default function DividaAtivaPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const identificador = queryParams.get('id') || queryParams.get('identificador') || queryParams.get('portal');
  const path = useMemo(() => `${config.api_path}/public/v1/transparencia/divida-ativa/${identificador || ''}`, [identificador]);

  const [page, setPage] = useState(1);
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFiltro, setShowFiltro] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ nome: '', exercicio: '' });
  const [filtroRequest, setFiltroRequest] = useState({ nome: '', exercicio: '' });

  const limit = 14;
  const lastPage = Math.ceil(count / limit);
  const queryString = getQueryString(filtroRequest);
  const xlsxUrl = `${path}/xlsx${queryString ? `?${queryString}` : ''}`;

  useEffect(() => {
    if (!identificador) return undefined;

    let active = true;
    setLoading(true);
    setError(null);

    Axios.get(`${path}?limit=${limit}&offset=${(page - 1) * limit}${queryString ? `&${queryString}` : ''}`)
      .then((response) => {
        if (!active) return;
        const data = response.data && response.data.data ? response.data.data : {};
        setList(data.list || []);
        setCount(data.count || 0);
      })
      .catch((requestError) => {
        if (active) setError(requestError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [identificador, page, path, queryString]);

  const handleBuscar = (event) => {
    event.preventDefault();
    setPage(1);
    setFiltroRequest({ ...filters });
  };

  const exportResults = async (type) => {
    setExporting(true);
    try {
      const response = await Axios.get(`${path}?limit=100000&offset=0${queryString ? `&${queryString}` : ''}`);
      const data = response.data && response.data.data ? response.data.data : {};
      const rows = getExportRows(data.list || []);
      if (type === 'csv') exportToCsv('divida-ativa.csv', exportHeaders, rows);
      else exportToTxt('divida-ativa.txt', exportHeaders, rows);
    } finally {
      setExporting(false);
    }
  };

  if (!identificador) return <Loading />;

  return (
    <Container fluid>
      <Card className='mt-4 mb-2'>
        <Card.Body>
          <div className='d-flex justify-content-between'>
            <div>
              <h3 className='font-weight-light my-0'>Dívida Ativa</h3>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className='my-2'>
        <Card.Header className='py-2' onClick={() => setShowFiltro(!showFiltro)} style={{ cursor: 'pointer' }}>
          <div className='d-flex justify-content-between'>
            <Card.Title className='my-0'>Filtros</Card.Title>
            <FontAwesomeIcon icon={showFiltro ? faMinus : faPlus} />
          </div>
        </Card.Header>
        <Collapse in={showFiltro}>
          <div>
            <Card.Body>
              <Form onSubmit={handleBuscar}>
                <Row className='mb-4'>
                  <Col md={6}>
                    <FormLabel>Nome do devedor</FormLabel>
                    <Form.Control
                      value={filters.nome}
                      onChange={(event) => setFilters({ ...filters, nome: event.target.value })}
                    />
                  </Col>
                  <Col md={3}>
                    <FormLabel>Exercício da inscrição</FormLabel>
                    <Form.Control
                      type='number'
                      value={filters.exercicio}
                      onChange={(event) => setFilters({ ...filters, exercicio: event.target.value })}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button type='submit' variant='outline-primary' className='mr-2'>Buscar</Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Card className='my-2'>
        <Card.Body className='py-2 d-flex align-items-center' style={{ gap: '0.6rem' }}>
          <Card.Title className='my-0'>Relatórios</Card.Title>
          <ButtonGroup>
            <Button variant='outline-primary' disabled={exporting} onClick={() => exportResults('txt')}>
              <FontAwesomeIcon icon={faFileAlt} /> TXT
            </Button>
            <Button variant='outline-primary' disabled={exporting} onClick={() => exportResults('csv')}>
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </Button>
            <Button variant='outline-primary' href={xlsxUrl} target='_blank' rel='noopener noreferrer'>
              <FontAwesomeIcon icon={faFileExcel} /> XLSX
            </Button>
          </ButtonGroup>
        </Card.Body>
      </Card>

      <main>
        <div className='table-responsive'>
          <Table className='table-bordered table-hover table-striped'>
            <thead>
              <tr>
                {tableColumns.map((column) => <th key={column.property} className='align-middle'>{column.header}</th>)}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr><td colSpan={tableColumns.length} className='table-danger'>Não foi possível carregar os registros.</td></tr>
              ) : list.map((item, index) => (
                <tr key={item.id || index}>
                  {tableColumns.map((column) => (
                    <td key={column.property}>
                      {column.format
                        ? column.format(item[column.property])
                        : (item[column.property] === null || item[column.property] === undefined ? '' : item[column.property])}
                    </td>
                  ))}
                </tr>
              ))}
              {!error && !loading && list.length === 0 && (
                <tr><td colSpan={tableColumns.length}>Nenhum registro encontrado.</td></tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className='d-flex w-100 justify-content-center'>
          <Pagination>
            {loading ? (
              <Pagination.Item disabled><FontAwesomeIcon icon='cog' spin /> Carregando...</Pagination.Item>
            ) : lastPage <= 0 ? (
              <Pagination.Item disabled>Nenhum registro encontrado</Pagination.Item>
            ) : (
              <Fragment>
                <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
                <Pagination.Item disabled>{page} de {lastPage}</Pagination.Item>
                <Pagination.Next onClick={() => setPage(page + 1)} disabled={page >= lastPage} />
                <Pagination.Last onClick={() => setPage(lastPage)} disabled={page >= lastPage} />
              </Fragment>
            )}
          </Pagination>
        </div>
      </main>
    </Container>
  );
}
